import { useCallback, useEffect, useState } from 'react';
import { formationsApi } from '../../api/resources';
import { SignPad } from '../../components/SignPad.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Pastille de chaque statut : libellé, encre, fond. */
const STATUTS = {
  EN_ATTENTE: ['En attente', C.amber, C.amberL],
  ACCEPTEE: ['Acceptée', C.green, C.greenL],
  REFUSEE: ['Refusée', C.red, C.redL],
};

const JOURS_COURS = {
  TRAVAIL: 'Jours de cours : payés comme du travail',
  VACANCES: 'Jours de cours : pris sur les vacances',
  NON_PAYES: 'Jours de cours : non payés',
};

const formulaireVide = () => ({
  intitule: '',
  organisme: '',
  dateDebut: '',
  dateFin: '',
  jours: '',
  prix: '',
  motif: '',
});

/** Les conventions acceptées qui attendent encore la signature de l'employé. */
export function conventionsASigner(demandes) {
  return (demandes || []).filter((d) => d && d.statut === 'ACCEPTEE' && !d.signeLe);
}

/** Un montant en francs : « CHF 1'450.00 ». */
const montant = (n) => DEVISE + ' ' + fmt(n);

/** Le jour seul (JJ.MM.AAAA), même quand le serveur envoie un horodatage. */
function leJour(iso) {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(iso))) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const deux = (n) => String(n).padStart(2, '0');
      return deux(d.getDate()) + '.' + deux(d.getMonth() + 1) + '.' + d.getFullYear();
    }
  }
  return dateSuisse(iso);
}

/** « Du 12.01.2027 au 14.01.2027 », ou la seule date connue. */
function periode(d) {
  if (d.dateDebut && d.dateFin && d.dateDebut !== d.dateFin) {
    return trf('Du {0} au {1}', dateSuisse(d.dateDebut), dateSuisse(d.dateFin));
  }
  return dateSuisse(d.dateDebut || d.dateFin);
}

const styleLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: C.grey,
  minWidth: 0,
};

const styleChamp = { ..._inp, fontSize: 14, minHeight: 44, boxSizing: 'border-box' };

const styleBloc = {
  background: C.white,
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const styleTitre = {
  fontSize: 12,
  fontWeight: 800,
  color: C.grey,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};

const styleLien = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  fontSize: 13,
  fontWeight: 700,
  color: C.teal,
  textDecoration: 'none',
};

/** La pastille du statut d'une demande. */
export function PuceStatut({ statut }) {
  const [libelle, encre, fond] = STATUTS[statut] || STATUTS.EN_ATTENTE;
  return (
    <span
      style={{
        flexShrink: 0,
        padding: '5px 10px',
        borderRadius: 99,
        background: fond,
        color: encre,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {tr(libelle)}
    </span>
  );
}

/** L'accord du bureau, en mots simples. */
function Accord({ demande: d }) {
  let paie = null;
  if (d.societePaie === 'TOUT') {
    paie = d.prix
      ? trf('La société paie : tout ({0})', montant(d.prix))
      : tr('La société paie : tout');
  } else if (d.societePaie === 'PARTIE') {
    paie = trf('La société paie : une partie : {0}', montant(d.montantSociete));
  } else if (d.societePaie === 'RIEN') {
    paie = tr('La société paie : rien');
  }
  const jours = JOURS_COURS[d.joursCours];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, color: C.ink }}>
      {paie && <div>{paie}</div>}
      {jours && <div>{tr(jours)}</div>}
      {Number(d.engagementMois) > 0 && <div>{trf('Engagement : {0} mois', d.engagementMois)}</div>}
      {d.engagementTexte && <div style={{ color: C.grey }}>{d.engagementTexte}</div>}
    </div>
  );
}

/** « À signer : Convention de formation » : lire, cocher, signer au doigt. */
function SignatureConvention({ demande, onSigne }) {
  const [accepte, setAccepte] = useState(false);
  const [trace, setTrace] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const pret = accepte && !!trace && !envoi;

  const signe = async () => {
    if (!pret) return;
    setErreur('');
    setEnvoi(true);
    try {
      await formationsApi.signe(demande.id, trace);
      await onSigne?.();
    } catch (e) {
      setErreur(e?.message || tr("La signature n'a pas pu être enregistrée. Réessaie."));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div
      data-testid="signer-convention"
      style={{
        border: '1.5px solid ' + C.amber,
        borderRadius: 12,
        padding: 12,
        background: C.amberL,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>
        {tr('À signer : Convention de formation')}
      </div>
      <a
        href={formationsApi.adresseApercu(demande.id)}
        target="_blank"
        rel="noreferrer"
        style={styleLien}
      >
        {tr('Lire la convention (PDF)')}
      </a>
      <label
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          minHeight: 44,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={accepte}
          onChange={(e) => setAccepte(e.target.checked)}
          style={{ width: 22, height: 22, flexShrink: 0 }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>
          {tr("J'ai lu et j'accepte cette convention.")}
        </span>
      </label>
      <SignPad onChange={setTrace} />
      {erreur && (
        <div role="alert" style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>
          {erreur}
        </div>
      )}
      <button
        type="button"
        onClick={signe}
        disabled={!pret}
        style={{
          minHeight: 48,
          width: '100%',
          border: 'none',
          borderRadius: 12,
          background: pret ? C.green : C.muted,
          color: C.white,
          fontSize: 15,
          fontWeight: 800,
          cursor: pret ? 'pointer' : 'not-allowed',
        }}
      >
        {envoi ? tr('Enregistrement…') : tr('Signer la convention')}
      </button>
    </div>
  );
}

/** Une demande de « Mes demandes ». */
function Demande({ demande: d, titulaire, onSigne }) {
  return (
    <div
      data-testid="demande-formation"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '10px 0',
        borderTop: '1px solid ' + C.border,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{d.intitule}</div>
          {(d.dateDebut || d.dateFin) && (
            <div style={{ fontSize: 12, color: C.grey }}>{periode(d)}</div>
          )}
          {d.demandeLe && (
            <div style={{ fontSize: 12, color: C.grey }}>
              {trf('Demandé le {0}', leJour(d.demandeLe))}
            </div>
          )}
        </div>
        <div>
          <PuceStatut statut={d.statut} />
        </div>
      </div>

      {d.statut === 'ACCEPTEE' && <Accord demande={d} />}

      {d.reponseTexte && (d.statut === 'ACCEPTEE' || d.statut === 'REFUSEE') && (
        <div style={{ fontSize: 13, color: C.grey }}>
          {trf('Réponse du bureau : {0}', d.reponseTexte)}
        </div>
      )}

      {d.statut === 'ACCEPTEE' && !d.signeLe && titulaire && (
        <SignatureConvention demande={d} onSigne={onSigne} />
      )}

      {d.statut === 'ACCEPTEE' && d.signeLe && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: C.green }}>
            {trf('✓ Convention signée le {0}', leJour(d.signeLe))}
          </span>
          <a
            href={formationsApi.adresseMaConvention(d.id)}
            target="_blank"
            rel="noreferrer"
            style={{ ...styleLien, color: C.green }}
          >
            {tr('📄 Ma convention signée (PDF)')}
          </a>
        </div>
      )}

      {d.nomFichier && (
        <a
          href={formationsApi.adresseMonProgramme(d.id)}
          target="_blank"
          rel="noreferrer"
          style={styleLien}
        >
          {tr('Voir le programme')}
        </a>
      )}
    </div>
  );
}

/**
 * « Ma formation » : l'employé demande un cours au bureau, suit ses demandes
 * et signe au doigt la convention d'une demande acceptée.
 *
 * @param {object} props
 * @param {boolean} [props.titulaire] faux quand le bureau regarde l'espace de
 *   l'employé : ni formulaire, ni signature
 * @returns {JSX.Element} la section « Ma formation »
 */
export function MesFormations({ titulaire = true }) {
  const [demandes, setDemandes] = useState(null);
  const [erreurListe, setErreurListe] = useState('');
  const [form, setForm] = useState(formulaireVide);
  const [fichier, setFichier] = useState(null);
  const [cleFichier, setCleFichier] = useState(0);
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const [erreur, setErreur] = useState('');

  const charge = useCallback(
    () =>
      Promise.resolve(formationsApi.mesDemandes())
        .then((liste) => {
          setDemandes(Array.isArray(liste) ? liste : []);
          setErreurListe('');
        })
        .catch((e) => {
          setDemandes((avant) => avant || []);
          setErreurListe(e?.message || tr('Impossible de charger tes demandes.'));
        }),
    [],
  );

  useEffect(() => {
    charge();
  }, [charge]);

  const change = (cle) => (e) => {
    const valeur = e.target.value;
    setForm((f) => ({ ...f, [cle]: valeur }));
    setEnvoye(false);
  };

  const envoie = async (e) => {
    e?.preventDefault?.();
    if (envoi) return;
    setErreur('');
    setEnvoye(false);
    if (!form.intitule.trim()) {
      setErreur(tr('Écris le nom du cours.'));
      return;
    }
    if (form.dateDebut && form.dateFin && form.dateFin < form.dateDebut) {
      setErreur(tr('La date de fin est avant la date de début.'));
      return;
    }
    const nombre = (v) => (String(v).trim() === '' ? undefined : Number(v));
    setEnvoi(true);
    try {
      await formationsApi.demande(
        {
          intitule: form.intitule.trim(),
          organisme: form.organisme.trim(),
          dateDebut: form.dateDebut,
          dateFin: form.dateFin,
          jours: nombre(form.jours),
          prix: nombre(form.prix),
          motif: form.motif.trim(),
        },
        fichier || undefined,
      );
      setForm(formulaireVide());
      setFichier(null);
      setCleFichier((n) => n + 1);
      setEnvoye(true);
      await charge();
    } catch (err) {
      setErreur(err?.message || tr("La demande n'a pas pu partir. Réessaie."));
    } finally {
      setEnvoi(false);
    }
  };

  const liste = demandes || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{tr('Ma formation')}</div>

      {titulaire && (
        <form
          onSubmit={envoie}
          aria-label={tr('Demander un cours')}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <div style={styleBloc}>
            <div style={styleTitre}>{tr('Demander un cours')}</div>
            <label style={styleLabel}>
              {tr('Quel cours ?')}
              <input
                value={form.intitule}
                onChange={change('intitule')}
                required
                maxLength={200}
                style={styleChamp}
              />
            </label>
            <label style={styleLabel}>
              {tr('École ou lieu')}
              <input
                value={form.organisme}
                onChange={change('organisme')}
                maxLength={200}
                style={styleChamp}
              />
            </label>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}
            >
              <label style={styleLabel}>
                {tr('Du')}
                <input
                  type="date"
                  value={form.dateDebut}
                  onChange={change('dateDebut')}
                  style={styleChamp}
                />
              </label>
              <label style={styleLabel}>
                {tr('Au')}
                <input
                  type="date"
                  value={form.dateFin}
                  min={form.dateDebut || undefined}
                  onChange={change('dateFin')}
                  style={styleChamp}
                />
              </label>
            </div>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}
            >
              <label style={styleLabel}>
                {tr('Jours de travail')}
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  inputMode="decimal"
                  value={form.jours}
                  onChange={change('jours')}
                  style={styleChamp}
                />
              </label>
              <label style={styleLabel}>
                {tr('Prix du cours (CHF)')}
                <input
                  type="number"
                  min="0"
                  step="0.05"
                  inputMode="decimal"
                  value={form.prix}
                  onChange={change('prix')}
                  style={styleChamp}
                />
              </label>
            </div>
            <label style={styleLabel}>
              {tr('Pourquoi ce cours ?')}
              <textarea
                value={form.motif}
                onChange={change('motif')}
                maxLength={1000}
                rows={3}
                style={{ ...styleChamp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </label>
            <label style={styleLabel}>
              {tr('Programme ou inscription (PDF, photo)')}
              <input
                key={cleFichier}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setFichier(e.target.files?.[0] || null)}
                style={{
                  ...styleChamp,
                  border: '1.5px dashed ' + C.border,
                  fontSize: 13,
                  color: C.grey,
                }}
              />
            </label>
          </div>
          {erreur && (
            <div role="alert" style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>
              {erreur}
            </div>
          )}
          {envoye && (
            <div role="status" style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>
              {tr("Demande envoyée. Le bureau te répond ici et ta cloche s'allume.")}
            </div>
          )}
          <button
            type="submit"
            disabled={envoi}
            style={{
              minHeight: 50,
              border: 'none',
              borderRadius: 14,
              background: envoi ? C.muted : C.teal,
              color: C.white,
              fontSize: 15,
              fontWeight: 800,
              cursor: envoi ? 'not-allowed' : 'pointer',
            }}
          >
            {envoi ? tr('Envoi…') : tr('Envoyer ma demande au bureau')}
          </button>
        </form>
      )}

      <div style={{ ...styleBloc, gap: 0 }}>
        <div style={{ ...styleTitre, marginBottom: 6 }}>{tr('Mes demandes')}</div>
        {erreurListe && (
          <div role="alert" style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>
            {erreurListe}
          </div>
        )}
        {demandes === null ? (
          <div style={{ fontSize: 13, color: C.grey }}>{tr('Chargement…')}</div>
        ) : liste.length === 0 ? (
          <div style={{ fontSize: 13, color: C.grey }}>{tr("Aucune demande pour l'instant.")}</div>
        ) : (
          liste.map((d) => (
            <Demande key={d.id} demande={d} titulaire={titulaire} onSigne={charge} />
          ))
        )}
      </div>
    </div>
  );
}
